/**
 * Solo follow camera: tracks the car with a velocity look-ahead, exponential
 * smoothing (frame-rate independent), and a slight speed-based zoom-out.
 */

import type { CarState } from '@microligue/shared';
import { clamp } from '@microligue/shared';

export interface CameraConfig {
  /** Seconds of velocity look-ahead. */
  lookAhead: number;
  /** 1/s — exponential smoothing stiffness for position. */
  stiffness: number;
  /** 1/s — exponential smoothing stiffness for zoom. */
  zoomStiffness: number;
  /** Zoom at standstill (world px → screen px factor at DPR 1). */
  baseZoom: number;
  /** 0..1 — fraction of zoom lost at max speed. */
  speedDezoom: number;
}

export function defaultCamera(): CameraConfig {
  return {
    lookAhead: 0.3,
    stiffness: 5.0,
    zoomStiffness: 3.0,
    baseZoom: 1.0,
    speedDezoom: 0.25,
  };
}

export interface CameraLike {
  x: number;
  y: number;
  zoom: number;
  applyTransform(ctx: CanvasRenderingContext2D, w: number, h: number, dpr: number): void;
}

export class FollowCamera {
  x = 0;
  y = 0;
  zoom = 1;
  private initialized = false;

  constructor(readonly config: CameraConfig) {}

  snapTo(car: CarState): void {
    this.x = car.x;
    this.y = car.y;
    this.zoom = this.config.baseZoom;
    this.initialized = true;
  }

  /** Continue smoothly from another camera's current view. */
  seed(x: number, y: number, zoom: number): void {
    this.x = x;
    this.y = y;
    this.zoom = zoom;
    this.initialized = true;
  }

  update(car: CarState, maxSpeed: number, dt: number): void {
    if (!this.initialized) this.snapTo(car);
    const cfg = this.config;
    const targetX = car.x + car.vx * cfg.lookAhead;
    const targetY = car.y + car.vy * cfg.lookAhead;
    const speed = Math.hypot(car.vx, car.vy);
    const speedRatio = clamp(speed / Math.max(maxSpeed, 1), 0, 1);
    const targetZoom = cfg.baseZoom * (1 - cfg.speedDezoom * speedRatio);

    // 1 - exp(-k·dt): exact exponential smoothing, independent of frame rate.
    const kPos = 1 - Math.exp(-cfg.stiffness * dt);
    const kZoom = 1 - Math.exp(-cfg.zoomStiffness * dt);
    this.x += (targetX - this.x) * kPos;
    this.y += (targetY - this.y) * kPos;
    this.zoom += (targetZoom - this.zoom) * kZoom;
    // A zoom ≤ 0 (bad config) would make the renderer's visible-area math
    // diverge into an infinite loop — never let it happen.
    this.zoom = clamp(this.zoom, 0.05, 8);
  }

  /** Applies the world→screen transform to the context. */
  applyTransform(
    ctx: CanvasRenderingContext2D,
    viewportW: number,
    viewportH: number,
    dpr: number,
  ): void {
    const z = this.zoom * dpr;
    ctx.setTransform(z, 0, 0, z, viewportW / 2 - this.x * z, viewportH / 2 - this.y * z);
  }
}

/**
 * Multiplayer "spectacle" camera: permanently frames every car — bounding box
 * of all competitors plus a comfortable margin, exponentially smoothed. Packed
 * field → close-up (capped); scattered field → widens up to the whole track.
 */
export class SpectacleCamera implements CameraLike {
  x = 0;
  y = 0;
  zoom = 1;

  /** px of world margin around the cars' bounding box. */
  private readonly margin = 190;
  /** Close-up ceiling — never zoom in tighter than this. */
  private readonly maxZoom = 1.05;
  private initialized = false;

  constructor(
    private readonly worldBounds: { minX: number; minY: number; maxX: number; maxY: number },
    private readonly stiffness = 4.0,
    private readonly zoomStiffness = 2.5,
  ) {}

  /** Continue smoothly from another camera's current view. */
  seed(x: number, y: number, zoom: number): void {
    this.x = x;
    this.y = y;
    this.zoom = zoom;
    this.initialized = true;
  }

  update(
    points: { x: number; y: number }[],
    viewportW: number,
    viewportH: number,
    dpr: number,
    dt: number,
  ): void {
    if (points.length === 0 || viewportW <= 0 || viewportH <= 0) return;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const p of points) {
      minX = Math.min(minX, p.x);
      minY = Math.min(minY, p.y);
      maxX = Math.max(maxX, p.x);
      maxY = Math.max(maxY, p.y);
    }
    minX -= this.margin;
    minY -= this.margin;
    maxX += this.margin;
    maxY += this.margin;

    const cssW = viewportW / dpr;
    const cssH = viewportH / dpr;
    // Floor: the whole track always fits.
    const wb = this.worldBounds;
    const minZoom = Math.min(
      cssW / Math.max(wb.maxX - wb.minX + this.margin, 1),
      cssH / Math.max(wb.maxY - wb.minY + this.margin, 1),
    );
    const fitZoom = Math.min(cssW / Math.max(maxX - minX, 1), cssH / Math.max(maxY - minY, 1));
    const targetZoom = Math.min(Math.max(fitZoom, minZoom), this.maxZoom);
    const targetX = (minX + maxX) / 2;
    const targetY = (minY + maxY) / 2;

    if (!this.initialized) {
      this.initialized = true;
      this.x = targetX;
      this.y = targetY;
      this.zoom = targetZoom;
      return;
    }
    const kPos = 1 - Math.exp(-this.stiffness * dt);
    const kZoom = 1 - Math.exp(-this.zoomStiffness * dt);
    this.x += (targetX - this.x) * kPos;
    this.y += (targetY - this.y) * kPos;
    this.zoom += (targetZoom - this.zoom) * kZoom;
  }

  applyTransform(
    ctx: CanvasRenderingContext2D,
    viewportW: number,
    viewportH: number,
    dpr: number,
  ): void {
    const z = this.zoom * dpr;
    ctx.setTransform(z, 0, 0, z, viewportW / 2 - this.x * z, viewportH / 2 - this.y * z);
  }
}
